import { z } from "zod";
import { EngagementId, PaymentId, UserId } from "./ids.js";
/**
 * Payments for a job (ADR 0011). A job is an engagement: the consumer is the requester and pays, the gig worker is
 * the provider and is paid straight into their own UPI account. GigSync never holds or moves the money; these records
 * are what both phones keep about it.
 */
/** NPCI's per-transaction cap for an ordinary UPI payment: ₹1,00,000, in paise. */
export const UPI_MAX_PAISE = 1_00_000 * 100;
export const PAISE_PER_RUPEE = 100;
export const PAYMENT_NOTE_MAX = 50;
export const PAYEE_NAME_MAX = 40;
/** A UPI ID (virtual payment address), in the form name@bank. */
export const Vpa = z.string().regex(/^[A-Za-z0-9._-]{2,256}@[A-Za-z][A-Za-z0-9]{1,63}$/, "UPI ID");
/** Money is always integer paise, never a float of rupees. */
export const Paise = z.number().int().positive().max(UPI_MAX_PAISE);
/** The 12-digit UPI transaction reference (UTR / RRN) the payer's app shows after paying. */
export const Utr = z.string().regex(/^\d{12}$/, "12-digit UPI reference");
export const PaymentMethod = z.enum(["upi", "cash"]);
/** requested → paid → confirmed, or disputed. Only the worker confirms or disputes. */
export const PaymentStatus = z.enum(["requested", "paid", "confirmed", "disputed"]);
const isoDate = z.string().datetime({ offset: true });
const note = z.string().max(PAYMENT_NOTE_MAX);
/**
 * The worker's UPI ID and the name payers see, kept once in the phone's settings. Never in a `Payment`, never on the
 * board, never on a server; it travels only sealed inside a `PaymentRequest` to the consumer of a signed job.
 */
export const Payee = z.object({ vpa: Vpa, name: z.string().trim().min(1).max(PAYEE_NAME_MAX) });
/** Body of a `payment_request` envelope: what the consumer's app needs to show the QR or open a UPI app. */
export const PaymentRequest = z.object({
    id: PaymentId,
    engagement: EngagementId,
    payee: UserId,
    payer: UserId,
    amount_paise: Paise,
    note,
    payee_vpa: Vpa,
    payee_name: z.string().min(1).max(PAYEE_NAME_MAX),
    requested_at: isoDate,
});
/** One payment for one job, as each phone keeps it. Never holds the worker's UPI ID. */
export const Payment = z.object({
    id: PaymentId,
    engagement: EngagementId,
    payee: UserId,
    payer: UserId,
    amount_paise: Paise,
    method: PaymentMethod,
    status: PaymentStatus,
    note,
    utr: Utr.optional(),
    requested_at: isoDate.optional(),
    paid_at: isoDate.optional(),
    confirmed_at: isoDate.optional(),
    disputed_at: isoDate.optional(),
});
/** Body of a `payment_record` envelope: the sender's view of a payment after a change. */
export const PaymentRecord = z.object({ payment: Payment });
//# sourceMappingURL=payment.js.map